"use client";

import styles from "./page.module.css";
import Switcher from "./components/switcher";
import PurposeSwitcher from "./components/purpose-switcher";
import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import MapViewLoading from "./components/map/map-view-loading";
import { CircleFlag } from "react-circle-flags";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { getCurrency } from "@/lib/currency-data/currency";
import Selector from "./components/selector";
import PhotoUploader from "./components/photo-uploader/photo-uploader";
import { useCurrenciesContext } from "@/global-context/currencies-context";
import {
  Currencies,
  ListingTypeEnum,
  PurposeEnum,
  TypeEnum,
} from "@/types/types";
import { editListingHandler, EditListingResponse } from "./handlers";
import { useLocale, useTranslations } from "next-intl";
import { useEdittingStore } from "./context/useEdittingStore";
import SelectorsWrapper from "./components/selectors-wrapper";
import { MdErrorOutline } from "react-icons/md";
import { useRouter } from "@/i18n/navigation";
import { toast } from "react-toastify";
import EditListingLoading from "./edit-listing-loading";
import { useMyListingsStore } from "../../useMyListingsStore";
import CategorySelector from "./components/category-selector";
import BuildingTypeSelector from "./components/building-type-selector";
import FacilitiesSelector from "./components/facilities-selector";
import ViewSelector from "./components/view-selector";
import OrientationSelector from "./components/orientation-selector";
import { makeLocalizedHrefNoLocale } from "@/lib/localizedRoutes/routeHelpers";
import VideoUploader from "./components/video-uploader/video-uploader";
import EPCSelector from "./components/epc-selector";

// const LazyMap = dynamic(() => import("./components/map/map-view"), {
//   ssr: false,
//   loading: () => <MapViewLoading />,
// });

const numberFormatter = new Intl.NumberFormat("de-DE", {
  maximumFractionDigits: 0,
});

const handleKeyDown = (e: any, value: number) => {
  if (e.key === "Backspace" && value === 0) {
    e.preventDefault();
    return;
  }
};

export default function EditListingLayout({
  listingId,
}: {
  listingId: string;
}) {
  const t = useTranslations("NewListing");
  const { userData } = useUserAuthDataContext();
  const userCurrency = getCurrency(userData.currency);
  const { currenciesRates } = useCurrenciesContext();
  const currencyKey = `${userCurrency.Name}-EUR` as keyof Currencies;
  const rate: number = currenciesRates[currencyKey];
  const initListing = useEdittingStore((state) => state.initListing);
  const isHydrated = useEdittingStore((state) => state.isHydrated);
  const changedFields = useEdittingStore((state) => state.changedFields);
  const locale = useLocale();

  useEffect(() => {
    initListing(listingId, userData.sessionId, locale);
  }, []);

  return (
    <main className={styles.main}>
      <div className={styles.pageWrapper}>
        {!isHydrated ? (
          <EditListingLoading />
        ) : (
          <div className={styles.contentWrapper}>
            <h2 className={styles.title}>{t("editListingTitle")}</h2>
            <h3 className={styles.description}>{t("keyFeatures")}</h3>
            <hr className={styles.separator} />
            <PurposeSwitcher
              forSalePlaceholder={t("forSale")}
              forRentPlaceholder={t("forRent")}
            />
            <Switcher
              apartamentPlaceholder={t("apartament")}
              housePlaceholder={t("house")}
              landPlaceholder={t("land")}
              officePlaceholder={t("offices")}
            />
            <div className={styles.categoryAndBuildingWrapper}>
              <CategorySelectorInput />
              <BuildingTypeSelectorInput />
            </div>
            <PriceInput userCurrency={userCurrency} t={t} rate={rate} />
            <TitleInput t={t} />
            <WebTourInput t={t} />
            <VideoUploaderInput />
            <PhotoUploader />
            <SelectorsWrapper t={t} />
            <div className={styles.inputsWrapper}>
              <div className={styles.inputsRow}>
                <InteriorSpaceInput t={t} unit_system={userData.unit_system} />
                <div className={styles.inputItem}>
                  <LotSizeInput t={t} unit_system={userData.unit_system} />
                </div>
                <LevelsInput t={t} />
              </div>
              <div className={styles.inputsRow}>
                <TotalStructureAreaInput
                  t={t}
                  unit_system={userData.unit_system}
                />
                <YearBuiltSelector />
                <BuyersFeeSelector />
              </div>
            </div>
            <MaterialsInput
              title={t("constructMaterials")}
              placeholder={t("constructMaterialsPlaceholder")}
            />
            <div className={styles.locationItemWrapper}>
              {/* <LazyMap /> */}
            </div>
            <div className={styles.inputItem}>
              <DescriptionTextArea t={t} />
            </div>
            <Epc />
            <FacilitiesSelectorInput />
            <OrientationSelectorInput />
            <ViewSelectorInput />
            {/* <InteriorAccordionWrapper /> */}
            {/* <ExteriorAccordion /> */}
            {/* <OtherAccordion /> */}
            <div className={styles.nextButtonWrapper}>
              <NextButton text={t("next")} rate={rate} locale={locale} />
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

function CategorySelectorInput() {
  const category = useEdittingStore((state) => state.listing.category);
  const categoryError = useEdittingStore(
    (state) => state.validationErrors.category,
  );
  const type = useEdittingStore((state) => state.listing.type);
  const setField = useEdittingStore((state) => state.setField);
  return (
    <CategorySelector
      activeValue={category}
      categoryError={categoryError}
      type={type}
      onClick={(newValue) => {
        setField("category", newValue);
      }}
    />
  );
}

function BuildingTypeSelectorInput() {
  const building_type = useEdittingStore(
    (state) => state.listing.building_type,
  );
  const office_type = useEdittingStore((state) => state.listing.office_type);
  const buildingTypeError = useEdittingStore(
    (state) => state.validationErrors.building_type,
  );
  const officeTypeError = useEdittingStore(
    (state) => state.validationErrors.office_type,
  );
  const type = useEdittingStore((state) => state.listing.type);
  const setField = useEdittingStore((state) => state.setField);
  return (
    <>
      {type === TypeEnum.APARTMENT || type === TypeEnum.OFFICE ? (
        <BuildingTypeSelector
          activeValue={
            type === TypeEnum.APARTMENT ? building_type : office_type
          }
          buildingTypeError={buildingTypeError}
          officeTypeError={officeTypeError}
          type={type}
          onClickBuildingType={(newValue) => {
            setField("building_type", newValue);
          }}
          onClickOfficeType={(newValue) => {
            setField("office_type", newValue);
          }}
        />
      ) : null}
    </>
  );
}

function PriceInput({
  t,
  userCurrency,
  rate,
}: {
  t: any;
  userCurrency: {
    Name: string;
    Code: string;
    FullName: string;
  };
  rate: number;
}) {
  const price = useEdittingStore((state) => state.listing.price);
  const priceError = useEdittingStore((state) => state.validationErrors.price);
  const setField = useEdittingStore((state) => state.setField);
  return (
    <>
      <h3
        id="price"
        className={`${styles.fieldTitle} ${priceError && styles.error}`}
      >
        {t("price")}*
      </h3>
      {priceError && (
        <p className={styles.errorText}>
          {t(priceError)}
          <span>
            <MdErrorOutline />
          </span>
        </p>
      )}
      <div className={`${styles.priceWrapper} ${priceError && styles.error}`}>
        <div className={styles.mainInput}>
          <div className={styles.iconWrapper}>
            <CircleFlag
              className={styles.icon}
              countryCode={userCurrency.Code}
            />
            <span>{userCurrency.Name}</span>
          </div>
          <input
            min={0}
            minLength={1}
            value={numberFormatter.format(price)}
            onChange={(e) => {
              const newValue = e.target.value.replace(/\D/g, "");
              setField("price", Number(newValue));
            }}
            type="text"
            className={styles.priceInput}
            placeholder={t("pricePlaceholder")}
          />
        </div>
        <div className={styles.approxPriceWrapper}>
          <span className={styles.approxPriceText}>
            ≈&nbsp;
            {numberFormatter.format(price * rate)}
          </span>
          <span className={styles.approxPriceDescription}>EUR</span>
          <CircleFlag className={styles.icon} countryCode={"eu"} />
        </div>
      </div>
    </>
  );
}

function TitleInput({ t }: { t: any }) {
  const title = useEdittingStore((state) => state.listing.title);
  const titleError = useEdittingStore((state) => state.validationErrors.title);
  const setField = useEdittingStore((state) => state.setField);
  return (
    <>
      <h3
        id="title"
        className={`${styles.fieldTitle} ${titleError && styles.error}`}
      >
        {t("listingTitle")}*
      </h3>
      {titleError && (
        <p className={styles.errorText}>
          {t(titleError)}
          <span>
            <MdErrorOutline />
          </span>
        </p>
      )}
      <div className={styles.titleInputWrapper}>
        <input
          value={title}
          onChange={(e) => {
            setField("title", e.target.value);
          }}
          type="text"
          className={`${styles.titleInput} ${titleError && styles.error}`}
          maxLength={90}
          placeholder={t("listingTitlePlaceholde")}
        />
        <span className={styles.textLength}>{title.length} / 90</span>
      </div>
    </>
  );
}

function WebTourInput({ t }: { t: any }) {
  const web_tour_url = useEdittingStore((state) => state.listing.web_tour_url);
  const tourError = useEdittingStore(
    (state) => state.validationErrors.web_tour_url,
  );
  const setField = useEdittingStore((state) => state.setField);
  const listing_type = useEdittingStore((state) => state.listing.listing_type);
  return (
    <>
      {listing_type === ListingTypeEnum.PRIME ||
      listing_type === ListingTypeEnum.SPOTLIGHT ? (
        <>
          <h3
            id="web_tour_url"
            className={`${styles.fieldTitle} ${tourError && styles.error}`}
          >
            {t("webTourTitle")}
          </h3>
          {tourError && (
            <p className={styles.errorText}>
              {t(tourError)}
              <span>
                <MdErrorOutline />
              </span>
            </p>
          )}
          <div className={styles.titleInputWrapper}>
            <input
              value={web_tour_url}
              onChange={(e) => {
                setField("web_tour_url", e.target.value);
              }}
              type="text"
              className={`${styles.titleInput} ${tourError && styles.error}`}
              placeholder={t("webTourPlaceholder")}
            />
          </div>
        </>
      ) : null}
    </>
  );
}

function VideoUploaderInput() {
  const listing_type = useEdittingStore((state) => state.listing.listing_type);
  return (
    <>{listing_type !== ListingTypeEnum.ESSENTIAL ? <VideoUploader /> : null}</>
  );
}

function InteriorSpaceInput({
  t,
  unit_system,
}: {
  t: any;
  unit_system: string;
}) {
  const interior_space = useEdittingStore(
    (state) => state.listing.interior_space,
  );
  const type = useEdittingStore((state) => state.listing.type);
  const interiorSpaceError = useEdittingStore(
    (state) => state.validationErrors.interior_space,
  );
  const setField = useEdittingStore((state) => state.setField);
  return (
    <>
      {type !== TypeEnum.LAND && (
        <div id="interior_space" className={styles.inputItem}>
          <h3
            className={`${styles.fieldTitle} ${
              interiorSpaceError && styles.error
            }`}
          >
            {t("interiorSpace")}*
          </h3>
          {interiorSpaceError && (
            <p className={styles.errorText}>
              {t(interiorSpaceError)}
              <span>
                <MdErrorOutline />
              </span>
            </p>
          )}
          <div
            className={`${styles.inputWrapper} ${
              interiorSpaceError && styles.error
            }`}
          >
            <input
              value={`${interior_space}`}
              onChange={(e) =>
                setField("interior_space", Number(e.target.value))
              }
              onKeyDown={(e) => handleKeyDown(e, interior_space)}
              type="number"
              placeholder={t("interiorPlaceholder")}
            />
            <span>{unit_system === "Metric" ? "mp" : "sqft"}</span>
          </div>
        </div>
      )}
    </>
  );
}

function TotalStructureAreaInput({
  t,
  unit_system,
}: {
  t: any;
  unit_system: string;
}) {
  const total_structure_area = useEdittingStore(
    (state) => state.listing.total_structure_area,
  );
  const totalStructureAreaError = useEdittingStore(
    (state) => state.validationErrors.total_structure_area,
  );
  const type = useEdittingStore((state) => state.listing.type);
  const setField = useEdittingStore((state) => state.setField);
  return (
    <>
      {type !== TypeEnum.LAND && (
        <div id="total_structure_area" className={styles.inputItem}>
          <h3
            className={`${styles.fieldTitle} ${
              totalStructureAreaError && styles.error
            }`}
          >
            {t("totalStructArea")}*
          </h3>
          {totalStructureAreaError && (
            <p className={styles.errorText}>
              {t(totalStructureAreaError)}
              <span>
                <MdErrorOutline />
              </span>
            </p>
          )}
          <div
            className={`${styles.inputWrapper} ${
              totalStructureAreaError && styles.error
            }`}
          >
            <input
              value={`${total_structure_area}`}
              onChange={(e) =>
                setField("total_structure_area", Number(e.target.value))
              }
              onKeyDown={(e) => handleKeyDown(e, total_structure_area)}
              type="number"
              placeholder={t("totalStructAreaPlaceholder")}
            />
            <span>{unit_system === "Metric" ? "mp" : "sqft"}</span>
          </div>
        </div>
      )}
    </>
  );
}

function MaterialsInput({
  title,
  placeholder,
}: {
  title: string;
  placeholder: string;
}) {
  const construction_materials = useEdittingStore(
    (state) => state.listing.construction_materials,
  );
  const type = useEdittingStore((state) => state.listing.type);
  const setField = useEdittingStore((state) => state.setField);
  return (
    <>
      {type !== TypeEnum.LAND && (
        <div
          id="construction_materials"
          className={`${styles.inputItem} ${styles.materialsItem}`}
        >
          <h3 className={styles.fieldTitle}>{title}</h3>
          <input
            value={construction_materials}
            onChange={(e) => {
              setField("construction_materials", e.target.value);
            }}
            type="text"
            className={styles.plainInput}
            placeholder={placeholder}
          />
        </div>
      )}
    </>
  );
}

function LevelsInput({ t }: { t: any }) {
  const levels = useEdittingStore((state) => state.listing.levels);
  const levelsError = useEdittingStore(
    (state) => state.validationErrors.levels,
  );
  const type = useEdittingStore((state) => state.listing.type);
  const setField = useEdittingStore((state) => state.setField);
  return (
    <>
      {type !== TypeEnum.LAND && (
        <div id="levels" className={styles.inputItem}>
          <h3 className={`${styles.fieldTitle} ${levelsError && styles.error}`}>
            {t("levels")}*
          </h3>
          {levelsError && (
            <p className={styles.errorText}>
              {t(levelsError)}
              <span>
                <MdErrorOutline />
              </span>
            </p>
          )}
          <input
            value={levels}
            onChange={(e) => {
              let inputValue = e.target.value;
              if (/^[0-9/]*$/.test(inputValue)) {
                const parts = inputValue.split("/");
                if (parts.length <= 2) {
                  setField("levels", inputValue);
                }
              }
            }}
            type="text"
            className={`${styles.plainInput} ${levelsError && styles.error}`}
            placeholder={"Ex: 20/25 or 25..."}
          />
        </div>
      )}
    </>
  );
}

function LotSizeInput({ t, unit_system }: { t: any; unit_system: string }) {
  const lot_size = useEdittingStore((state) => state.listing.lot_size);
  const lotSizeError = useEdittingStore(
    (state) => state.validationErrors.lot_size,
  );
  const setField = useEdittingStore((state) => state.setField);
  return (
    <>
      <h3
        id="lot_size"
        className={`${styles.fieldTitle} ${lotSizeError && styles.error}`}
      >
        {t("lotSize")}*
      </h3>
      {lotSizeError && (
        <p className={styles.errorText}>
          {t(lotSizeError)}
          <span>
            <MdErrorOutline />
          </span>
        </p>
      )}
      <div className={`${styles.inputWrapper} ${lotSizeError && styles.error}`}>
        <input
          value={`${lot_size}`}
          onChange={(e) => setField("lot_size", Number(e.target.value))}
          onKeyDown={(e) => handleKeyDown(e, lot_size)}
          type="number"
          placeholder={t("lotSizePlaceholder")}
        />
        <span>{unit_system === "Metric" ? "mp" : "sqft"}</span>
      </div>
    </>
  );
}

function YearBuiltSelector() {
  const year_built = useEdittingStore((state) => state.listing.year_built);
  const yearBuiltError = useEdittingStore(
    (state) => state.validationErrors.year_built,
  );
  const type = useEdittingStore((state) => state.listing.type);
  const setField = useEdittingStore((state) => state.setField);
  return (
    <>
      {type !== TypeEnum.LAND && (
        <Selector
          activeValue={year_built}
          isYearBuilt={true}
          yearBuiltError={yearBuiltError}
          onClick={(newValue) => {
            setField("year_built", newValue);
          }}
        />
      )}
    </>
  );
}

function BuyersFeeSelector() {
  const buyers_fee = useEdittingStore((state) => state.listing.buyers_fee);
  const buyersFeeError = useEdittingStore(
    (state) => state.validationErrors.buyers_fee,
  );
  const purpose = useEdittingStore((state) => state.listing.purpose);
  const setField = useEdittingStore((state) => state.setField);
  return (
    <>
      {purpose !== PurposeEnum.FORRENT && (
        <Selector
          activeValue={buyers_fee}
          isYearBuilt={false}
          buyersFeeError={buyersFeeError}
          onClick={(newValue) => {
            setField("buyers_fee", newValue);
          }}
        />
      )}
    </>
  );
}

function FacilitiesSelectorInput() {
  const facilitiesValue = useEdittingStore((state) => state.listing.facilities);
  const hasLuxuryFinishesValue = useEdittingStore(
    (state) => state.listing.has_luxury_finishes,
  );
  const facilitiesError = useEdittingStore(
    (state) => state.validationErrors.facilities,
  );
  const type = useEdittingStore((state) => state.listing.type);
  const purpose = useEdittingStore((state) => state.listing.purpose);
  return (
    <FacilitiesSelector
      facilitiesValue={facilitiesValue}
      hasLuxuryFinishesValue={hasLuxuryFinishesValue}
      error={facilitiesError}
      type={type}
      purpose={purpose}
    />
  );
}

function OrientationSelectorInput() {
  const orientationValue = useEdittingStore(
    (state) => state.listing.orientation,
  );
  const orientationError = useEdittingStore(
    (state) => state.validationErrors.orientation,
  );
  const type = useEdittingStore((state) => state.listing.type);
  const setField = useEdittingStore((state) => state.setField);
  return (
    <>
      {type !== TypeEnum.LAND && (
        <OrientationSelector
          currentOrientation={orientationValue}
          error={orientationError}
          onClick={(newValue) => {
            setField("orientation", newValue);
          }}
        />
      )}
    </>
  );
}

function ViewSelectorInput() {
  const viewValue = useEdittingStore((state) => state.listing.view);
  const viewError = useEdittingStore((state) => state.validationErrors.view);
  const type = useEdittingStore((state) => state.listing.type);
  const setField = useEdittingStore((state) => state.setField);
  return (
    <>
      {type !== TypeEnum.LAND && (
        <ViewSelector
          currentView={viewValue}
          error={viewError}
          onClick={(newValue) => {
            setField("view", newValue);
          }}
        />
      )}
    </>
  );
}

function DescriptionTextArea({ t }: { t: any }) {
  const description = useEdittingStore((state) => state.listing.description);
  const descriptionError = useEdittingStore(
    (state) => state.validationErrors.description,
  );
  const setField = useEdittingStore((state) => state.setField);
  return (
    <>
      <h3
        id="description"
        className={`${styles.fieldTitle} ${descriptionError && styles.error}`}
      >
        {t("description")}*
      </h3>
      {descriptionError && (
        <p className={styles.errorText}>
          {t(descriptionError)}
          <span>
            <MdErrorOutline />
          </span>
        </p>
      )}
      <div
        className={`${styles.textAreaWrapper} ${
          descriptionError && styles.error
        }`}
      >
        <textarea
          className={styles.descriptionTextArea}
          value={description}
          onChange={(e) => {
            setField("description", e.target.value);
          }}
          name="description"
          placeholder={t("descriptionPlaceholder")}
          maxLength={1600}
        />
        <span className={styles.textLength}>{description.length} / 1600</span>
      </div>
    </>
  );
}

function Epc() {
  const type = useEdittingStore((state) => state.listing.type);
  return <>{type !== TypeEnum.LAND && <EPCSelector />}</>;
}

function NextButton({
  text,
  rate,
  locale,
}: {
  text: string;
  rate: number;
  locale: string;
}) {
  const handleValidateFields = useEdittingStore(
    (state) => state.validateFields,
  );
  const { userData } = useUserAuthDataContext();
  const listingState = useEdittingStore((state) => state.listing);
  const changedFieldsState = useEdittingStore((state) => state.changedFields);
  const refetchUpdatedMyListings = useMyListingsStore(
    (state) => state.refetchEditedListing,
  );
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const router = useRouter();
  return (
    <button
      onClick={async (e) => {
        setIsButtonDisabled(true);
        e.preventDefault();
        const { validationErrors, firstErrorKey } = handleValidateFields();
        if (Object.keys(validationErrors).length === 0) {
          //if success go to /my-listings and refetch listing data else show toast
          const response = await editListingHandler(
            userData.sessionId,
            listingState,
            changedFieldsState,
            rate,
            userData.unit_system,
          );
          if (response == EditListingResponse.SUCCESS) {
            refetchUpdatedMyListings(
              listingState.listing_id,
              userData.sessionId,
              locale,
            );
            router.push(makeLocalizedHrefNoLocale(locale, "my-listings"));
          }
        } else {
          if (firstErrorKey) {
            const el = document.getElementById(firstErrorKey);
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
              el.focus();
            }
          }
          toast("There are errors in your form.", { type: "error" });
        }
        setIsButtonDisabled(false);
      }}
      disabled={isButtonDisabled}
      className={styles.nextButton}
    >
      {text}
    </button>
  );
}
